import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  computed,
  signal,
  inject,
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { EdeskService, MessageHeaderExt } from '../../core/edesk.service';
import { GetMessageHeadersResponse } from '../../api/model/getMessageHeadersResponse';
import { MessageHeader } from '../../api/model/messageHeader';
import { DotNetToDatePipe } from '../../shared/dot-net-to-date.pipe';
import { AuthService } from '../../core/auth/auth.service';

@Component({
  selector: 'app-message-list',
  standalone: true,
  imports: [CommonModule, DatePipe, DotNetToDatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './message-list.component.html',
  // Styles handled globally in styles.scss
})
export class MessageListComponent implements OnChanges {
  private authService = inject(AuthService);
  
  @Input() folderId: number | null = null;
  @Input() selectedMessageId: number | null = null;
  @Input() login: string | null = null;
  @Input() pageSize = 10;
  
  @Input() set query(val: string) {
    this._query.set(val || '');
  }

  @Output() messageSelected = new EventEmitter<{id: string, class: string}>();

  private rows = signal<MessageHeader[]>([]);
  loading = signal(false);
  error = signal<string | null>(null);

  private _query = signal('');
  private _hideTech = signal(false);

  private total = 0;
  private pageIndex = signal(0);

  /** filtered rows for the template */
  private filtered = computed(() => {
    let arr = this.rows();
    
    if (this._hideTech()) {
      arr = arr.filter(m => m.Class !== 'TECHNICAL');
    }

    const q = this._query().trim().toLocaleLowerCase();
    if (q) {
      arr = arr.filter(
        (m) =>
          (m.Title ?? '').toLocaleLowerCase().includes(q) ||
          this.displaySender(m).toLocaleLowerCase().includes(q)
      );
    }

    // 3. Ensure Sort (Newest to Oldest) is maintained even after filtering
    // (This is redundant if rows() is sorted, but safe to keep UI consistent)
    return this.sortMessagesDesc(arr);
  });

  headers() { return this.filtered(); }

  private requestSeq = 0;

  constructor(private api: EdeskService) {}

  ngOnChanges(ch: SimpleChanges): void {
    if (ch['folderId'] || ch['login'] || ch['pageSize']) {
      if (ch['pageSize']) {
        this.pageIndex.set(0);
      }
      this.resetPaging();
      if (this.folderId != null) this.fetchPage(true);
    }
  }

  refresh(): void {
    this.resetPaging();
    if (this.folderId != null) this.fetchPage(true);
  }

  openMessage(id: number | undefined, messageClass: string | undefined): void {
    if (id == null) return;
    this.messageSelected.emit({ id: String(id), class: messageClass ?? '' });
  }

  // --- SORTING LOGIC ---
  
  /**
   * Sorts messages by DateDelivery descending (Newest first)
   */
  private sortMessagesDesc(items: MessageHeader[]): MessageHeader[] {
    // Create a shallow copy to avoid mutating strict readonly arrays if necessary
    return [...items].sort((a, b) => {
      const dateA = a.DateDelivery ? new Date(a.DateDelivery).getTime() : 0;
      const dateB = b.DateDelivery ? new Date(b.DateDelivery).getTime() : 0;
      return dateB - dateA; // Large timestamp (New) - Small timestamp (Old)
    });
  }

  // --- API & DATA LOADING ---

  private resetPaging() {
    this.rows.set([]);
    this.total = 0;
    this.pageIndex.set(0);
    this.loading.set(false);
    this.error.set(null);
    this.requestSeq++;
  }

  private fetchPage(reset = false) {
    if (this.folderId == null) return;
    const seq = this.requestSeq;
    this.loading.set(true);

    // Pouzit login z tokenu (sub) alebo fallback na hardcoded hodnotu
    const skip = this.pageIndex() * this.pageSize;

    this.api
      .getMessageHeaders(this.folderId, this.pageSize, skip)
      .subscribe({
        next: (res: GetMessageHeadersResponse | any) => {
          if (seq !== this.requestSeq) return;

          let vals: MessageHeader[] = [];
          let total: number | undefined;

          // 1. Handle Real API Response
          if (Array.isArray((res as any)?.Values)) {
            let rawVals = (res as any).Values as MessageHeader[];
            // Enforce sorting immediately on received data
            vals = this.sortMessagesDesc(rawVals);
            total = (res as any).TotalCount ?? vals.length;
          } 
          // 2. Handle Demo JSON Response
          else if (Array.isArray((res as any)?.Messages)) {
            type DemoMessage = MessageHeader & { FolderId?: number };
            const all = ((res as any).Messages as DemoMessage[])
              .filter((m) => m.FolderId === this.folderId);
            
            // Sort ALL messages before slicing for pagination
            const sortedAll = this.sortMessagesDesc(all);

            total = sortedAll.length;
            vals = sortedAll.slice(skip, skip + this.pageSize);
          }

          this.rows.set(vals);
          this.total = total ?? vals.length;
          this.loading.set(false);
        },
        error: (err) => {
          if (seq !== this.requestSeq) return;
          console.error('getMessageHeaders failed', err);
          this.error.set('Nepodarilo sa načítať správy.');
          this.loading.set(false);
        },
      });
  }

  // --- HELPERS ---

  displaySender(m: MessageHeaderExt): string {
    const senderUri = m.SenderId;
    if (!senderUri) return '';
    const parts = senderUri.split('/');
    return parts[parts.length - 1] || senderUri;
  }

  isUnread(m: MessageHeader): boolean {
    return m.Class !== 'ACK';
  }

  statusTag(m: MessageHeader): { text: string; class: string } | null {
    switch (m.Class) {
      case 'ACK':
        return { text: 'DORUČENKA', class: 'app-tag--orange' };
      case 'TECHNICAL':
        return { text: 'TECHNICKÁ SPRÁVA', class: 'app-tag--blue' };
      default:
        return null;
    }
  }

  ariaLabelFor(m: MessageHeader): string {
    const from = this.displaySender(m);
    const subj = m.Title || '(bez predmetu)';
    return `${this.isUnread(m) ? 'Neprečítané,' : ''} Odosielateľ ${from}, Predmet ${subj}`;
  }

  isSelected(m: MessageHeader): boolean {
    return m.IdMessage === this.selectedMessageId;
  }

  // --- PAGING HELPERS ---
  
  page(): number { return this.pageIndex() + 1; }
  pages(): number { 
    // If filtering is active, show only 1 page (client-side filtered)
    if (this._query().trim() || this._hideTech()) {
      return 1;
    }
    return Math.max(1, Math.ceil(this.total / this.pageSize || 1)); 
  }
  canPrev(): boolean { 
    // No pagination when filtering
    if (this._query().trim() || this._hideTech()) return false;
    return this.pageIndex() > 0; 
  }
  canNext(): boolean { 
    // No pagination when filtering
    if (this._query().trim() || this._hideTech()) return false;
    return this.pageIndex() + 1 < this.pages(); 
  }

  prevPage(): void {
    if (!this.canPrev()) return;
    this.pageIndex.update(i => i - 1);
    this.fetchPage(true);
  }

  nextPage(): void {
    if (!this.canNext()) return;
    this.pageIndex.update(i => i + 1);
    this.fetchPage(true);
  }

  gotoPage(p: number): void {
    const idx = p - 1;
    if (idx < 0 || idx >= this.pages()) return;
    this.pageIndex.set(idx);
    this.fetchPage(true);
  }

  pagesArray(): (number | string)[] {
    const totalPages = this.pages();
    const cur = this.page();
    
    if (totalPages <= 7) {
      // Show all pages if 7 or fewer
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    
    // Show: 1 2 3 ... cur-1 cur cur+1 ... last-1 last
    const pages: (number | string)[] = [];
    
    // Always show first 2 pages
    pages.push(1);
    if (totalPages > 1) pages.push(2);
    
    // Show ellipsis if current page is far from start
    if (cur > 4) {
      pages.push('...');
    }
    
    // Show current page and neighbors (if not already shown)
    for (let i = Math.max(3, cur - 1); i <= Math.min(totalPages - 2, cur + 1); i++) {
      if (!pages.includes(i)) {
        pages.push(i);
      }
    }
    
    // Show ellipsis if current page is far from end
    if (cur < totalPages - 3) {
      pages.push('...');
    }
    
    // Always show last 2 pages
    if (totalPages > 2 && !pages.includes(totalPages - 1)) {
      pages.push(totalPages - 1);
    }
    if (!pages.includes(totalPages)) {
      pages.push(totalPages);
    }
    
    return pages;
  }
  
  firstPage(): void {
    this.gotoPage(1);
  }
  
  lastPage(): void {
    this.gotoPage(this.pages());
  }
  
  canGoFirst(): boolean {
    return this.page() > 1;
  }
  
  canGoLast(): boolean {
    return this.page() < this.pages();
  }

  rangeStart(): number {
    const filteredTotal = this.filtered().length;
    if (filteredTotal === 0) return 0;
    return 1;
  }
  rangeEnd(): number {
    return this.filtered().length;
  }
  totalCount(): number {
    return this.filtered().length;
  }
}